import { WorkflowHearingRescheduleRequestForm } from '@app/generated/contracts/workflow-hearing-reschedule-request.generated';
import type { FormIssues } from '@template-ui/helpers/generated-form.helpers';
import type {
  WorkflowClientAction,
  WorkflowClientCommandModule,
} from './workflow-command-registry';

function targetContext(action: WorkflowClientAction) {
  const initial = action.form?.initialValues;
  const scheduledProceedingId = initial?.['scheduledProceedingId'];
  const targetProceedingId = initial?.['targetProceedingId'];
  const expectedTargetETag = initial?.['expectedTargetETag'];
  return typeof scheduledProceedingId === 'number' &&
    Number.isSafeInteger(scheduledProceedingId) &&
    scheduledProceedingId > 0 &&
    targetProceedingId === scheduledProceedingId &&
    typeof expectedTargetETag === 'string' &&
    expectedTargetETag.trim()
    ? { scheduledProceedingId, targetProceedingId, expectedTargetETag }
    : null;
}

/** A hearing reference selects the record; the server supplies its current concurrency context. */
export const workflowHearingRescheduleCommandModule: WorkflowClientCommandModule =
  {
    code: 'phms.scheduling',
    version: 1,
    commands: [
      {
        key: {
          provider: 'scheduling',
          kind: 'command',
          code: 'scheduling.revise',
          version: 1,
        },
        form: WorkflowHearingRescheduleRequestForm,
        endpointSuffix: '/record-actions/hearing-reschedule',
        submitLabel: 'Save hearing schedule',
        submissionHint:
          'Times are shown in your local timezone and saved in UTC. Updates the linked hearing. Moving outside its current calendar slot releases that booking; room, timezone, panel and resources are retained and rechecked. Calendar rebooking is separate. This step stays open until you complete it.',
        confirmation: 'Hearing schedule saved. This step is still open.',
        formForAction: () => ({
          ...WorkflowHearingRescheduleRequestForm,
          fields: WorkflowHearingRescheduleRequestForm.fields.map((field) =>
            field.name === 'scheduledProceedingId'
              ? { ...field, readOnly: true }
              : field,
          ),
        }),
        bindContext: (context, action) => {
          const target = targetContext(action);
          return target
            ? {
                occurrenceId: context.occurrenceId,
                expectedETag: context.stepETag,
                ...target,
              }
            : null;
        },
        draftContextKey: (action) => {
          const target = targetContext(action);
          return target ? String(target.scheduledProceedingId) : null;
        },
        validateInput: (_action, values): FormIssues => {
          const instant = (value: unknown) =>
            value instanceof Date
              ? value.getTime()
              : typeof value === 'string'
                ? Date.parse(value)
                : Number.NaN;
          const start = instant(values['scheduledStartUtc']);
          const end = instant(values['scheduledEndUtc']);
          return Number.isFinite(start) && Number.isFinite(end) && end <= start
            ? { scheduledEndUtc: 'Choose an end time after the start time.' }
            : {};
        },
      },
    ],
  };
